import { useEffect, useState } from 'react';
import ReactLoading from 'react-loading';
import { useNavigate, useParams } from 'react-router-dom';
import DetailsImageDisplay from '../../components/detailsImageDisplay/DetailsImageDisplay';
import EmptyPage from '../../components/empty-page/emptyPage';
import BackIcon from '../../assets/icons/trvto_ArrowBack.svg';
import { toPascalCase } from '../../utils/pascalCase';
import { orgGetAccommodationById } from '../../api/orgAccommodation';
import { buildFormDataFromAccommodation } from '../../utils/accommodationPayload';
import ReviewSubmit from '../accommodation/review/ReviewSubmit';
import errorHandle from '../../utils/errorHandle';

/*
 * Organizer's own "view accommodation" page, opened by clicking a
 * card in My Packages (OrgCampPackList) - route /details/:tripId
 * under OrgCampLayout.
 *
 * This used to never fetch anything at all: its data-loading effect
 * was commented out, so packData stayed {} forever and the page
 * always rendered the empty state, no matter which listing you
 * opened. What was left behind it was also entirely trip-shaped
 * (days/nights, quickView.*, a "mark unavailable date" calendar
 * tied to trip date-blocking APIs) and would have been wrong for
 * accommodation data even with the fetch restored.
 *
 * Rebuilt to actually load the listing via
 * GET org/accommodation/:id (org-scoped - only returns listings
 * this organizer owns) and to present it the way the trip details
 * page presents a trip: a heading strip, the same image-carousel
 * component trip details uses, and then the full listing broken
 * into sections below. The section breakdown reuses ReviewSubmit
 * (the accommodation wizard's own Review step) in its read-only
 * mode - the same component and the same read-only mode already
 * used for support's accommodation details pages, so this doesn't
 * duplicate that rendering a third time.
 *
 * No booking/date-management UI here: accommodation bookings aren't
 * live yet, so there's nothing real for it to manage.
 */
export default function OrgCampDetails() {
    const navigate = useNavigate();
    const { tripId } = useParams();
    const [packData, setPackData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        if (!tripId) {
            setLoading(false);
            return undefined;
        }

        let cancelled = false;

        async function load() {
            setLoading(true);

            try {
                const result = await orgGetAccommodationById(tripId);

                if (!cancelled) {
                    setPackData(result?.data?.data || null);
                }
            } catch (error) {
                if (!cancelled) {
                    errorHandle(error);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        load();

        return () => {
            cancelled = true;
        };
    }, [tripId]);

    const formData = packData ? buildFormDataFromAccommodation(packData) : null;

    const galleryImages = packData?.media?.length
        ? [...packData.media]
              .sort((a, b) => (a?.isCover === b?.isCover ? 0 : a?.isCover ? -1 : 1))
              .map((m) => m.url)
              .filter(Boolean)
        : [];

    return (
        <div>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <button className='backBtn' onClick={() => navigate('/org/c/my-camps')}>
                <img src={BackIcon} alt='back' /> Back to Packages
            </button>
            {formData ? (
                <>
                    <div className='orgTripDetailsHeadingDiv'>
                        <div className='flex'>
                            <h1 className='detailsImageDisplayName'>{packData?.title || 'N/A'}</h1>
                        </div>
                        <div>
                            <div className='tripBriefInfo'>
                                <span className='tripDetailsSubDetails tripDetailsOrgName'>
                                    {packData?.organizerName ?? 'N/A'}
                                </span>
                                <span className='tripDetailsDot'></span>
                                <span className='tripDetailsSubDetails'>
                                    {packData?.propertyType ? toPascalCase(packData.propertyType) : 'N/A'}
                                </span>
                                <span className='tripDetailsDot'></span>
                                <span className='tripDetailsSubDetails'>
                                    {[packData?.address?.city, packData?.address?.state].filter(Boolean).join(', ') ||
                                        'N/A'}
                                </span>
                                <span className='tripDetailsDot'></span>
                                <span className='tripDetailsSubDetails'>
                                    {toPascalCase(packData?.status || 'N/A')}
                                </span>
                            </div>
                        </div>
                    </div>

                    {galleryImages.length > 0 && <DetailsImageDisplay imageData={galleryImages} />}

                    <ReviewSubmit formData={formData} readOnly />
                </>
            ) : (
                !loading && <EmptyPage />
            )}
        </div>
    );
}
